import { useState, useCallback, useEffect, Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import BackBar from '../../components/BackBar';
import QRCode from '../../components/QRCode';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Table, { ALIGN_TABLE } from '../../components/Table';
import InfoDescription from '../../components/InfoDescription';
import Badge, { BADGE_TYPE } from '../../components/Badge';
import Checkbox from '../../components/Checkbox';
import Modal from '../../components/Modal';
import TicketView from './ticket_view';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import { PriceType } from '../../models/types';
import Phone from '../../components/Phone';
import log from '../../utils/logger';
import Input from '../../components/Input';
import ErrorHint from '../../components/ErrorHint';
import Field from '../../components/Field';
import { createEvent } from '../../utils/events';
import { jwtDecode } from "jwt-decode";
import { arrayEquals, env, getNumber } from '../../utils/util';
import { WebSocketContext } from '../../store/WebSocketProvider';
import { UserContext } from '../../store/UserProvider';

const secret = env('JWT_SECRET') || '29036044-a489-4712-a50d-89c3e82ebdda-04325c04-03d6-44a6-949f-37798aeaf2e3';
export const SHARED_API_KEY_SECRET = env('JWT_SHARED_SECRET') || '50f2a4b4-cfdd-42aa-b643-dd31ef6be9c9-1e884dc0-2ef3-40a0-bceb-6f09b447ff1f';



const Place = ({ place, schedule, refresh }) => {
  const {t} = useTranslation(['common', 'route']);

  const repository = useContext(RepositoryContext);
  const userInfo = useContext(UserContext);
  const topic = useContext(WebSocketContext);

  const [showTicketViewModal, setShowTicketViewModal] = useState(false);

  const [accountId] = userInfo.useSession('account');

  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [ticketToken, setTicketToken] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [newSeats, setNewSeats] = useState([]);
  const [topicOrders, setTopicOrders] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseTicketViewModal = useCallback(() => {
    setShowTicketViewModal(false);
  }, []);

  const selectTicket = (ticket) => {
    setTicket(ticket);
    setShowTicketViewModal(true);
  };

  const handleSearchTicket = () => {
    const ticket = tickets.find((order) => order.number === ticketNumber);
    if (ticket) {
      setTicket(ticket);
      setShowTicketViewModal(true);
      setError('');
    } else {
      setError(t('ticket:error.ticket_not_found'));
    }
  };


  const prepareTicketInfo = (order) => {
    order.count = {
      adult: 0,
      adultActualCount: 0,
      child: 0,
      childActualCount: 0,
      noseat: 0,
      noseatActualCount: 0,
      count: 0,
      actualCount: 0,
    };

    order.prices = [];
    order.leftToPay = 0;

    order.passengers.forEach((price) => {
      order.leftToPay += price.actualPrice.leftToPay * price.count;
      order.prices.push(price);

      if (price.price.priceType === PriceType.NORMAL) {
        order.count.adult += price.count;
        order.count.adultActualCount += price.actualCount;
        order.count.count += price.count;
        order.count.actualCount += price.actualCount;
      }

      if (price.price.priceType === PriceType.CHILD) {
        if (price.price.noSeat) {
          order.count.noseat += price.count;
          order.count.noseatActualCount += price.actualCount;
        } else {
          order.count.child += price.count;
          order.count.childActualCount += price.actualCount;
          order.count.count += price.count;
          order.count.actualCount += price.actualCount;
        }
      }
    });
  };

  const handleScanTicket = (token) => {
    let jwtSecret = secret;
    try {
      const decoded = jwtDecode(token);
      const header = jwtDecode(token, { header: true });

      if (decoded && decoded._id) {
        repository.request(API.order.order, {id: decoded._id}).then((order) => {
          if (schedule.id === order.schedule.id) {
            prepareTicketInfo(order);

            setTicket(order);
            setShowTicketViewModal(true);
            setError('');
          } else {
            setTicket(null);
            setTicketToken('');
            setError(t('ticket:error.ticket_not_found'));
          }
        }).catch((e) => {
          setTicket(null);
          setTicketToken('');
          setError(t('ticket:error.ticket_not_found'));
          // setError(t('route:place.error.invalid_order'));
        });
      }
    } catch(e) {
      log.error(e);
    }
    // jwt.verify(token, jwtSecret, (err, decoded) => {
    //   if (err) {
    //     console.error(err);
    //   }

    //   console.log(decoded);
    // });
  };

  const handleBoardTicket = () => {
    setIsLoading(true);

    repository.request(API.guide.seat, {orderId: ticket.id, ...newSeats}).then(() => {
      setIsLoading(false);
      loadTickets();
      typeof refresh === 'function' && refresh(ticket.place.id, schedule.id);
    }).catch((e) => {
      setIsLoading(false);
      setError(t('ticket:error.not_enough_seat'));
    });

    setShowTicketViewModal(false);
    setTicket(null);
    createEvent('camera_start', true);
  };

  const refreshOrder = useCallback((order) => {
    if (order) {
      typeof refresh === 'function' && refresh(order.place?.id, order.schedule?.id);
    }
  }, [topicOrders]);

  const loadTickets = () => {
    let _tickets = [];

    repository.list(API.guide.schedule, { scheduleId: schedule.id }).then((orders) => {
      let newTopicOrders = orders.map(o=>o.id).sort();
      if (!arrayEquals(topicOrders, newTopicOrders)) {
        setTopicOrders(newTopicOrders);
      }

      orders.forEach((order) => {
        prepareTicketInfo(order);
        _tickets.push(order);
      });
      setTickets(_tickets);
      setTicket(null);
    }).catch(() => {
      setTickets([]);
      setTicket(null);
    });
  };

  useEffect(() => {
    if (ticketToken) {
      handleScanTicket(ticketToken);
    }
  }, [ticketToken]);

  useEffect(() => {
    topicOrders.forEach((orderId) => {
      // topic?.subscribe(`accounts.${accountId}.orders.${orderId}`, refreshOrder);
    });

    return (prevState) => {
      topicOrders.forEach((orderId) => {
        // topic?.unsubscribe(`accounts.${accountId}.orders.${orderId}`, refreshOrder);
      });
      };
  }, [topicOrders]);

  useEffect(() => {
    loadTickets();
  }, []);

  return (
    <>
      <Field title={t('route:place.scan_qr')}>
        <QRCode.Scanner
          onResult={(result, error, reader) => {
            if (result) {
              setTicketToken(result);
              reader.stop();
            }

            if (error) {
              log.error(error);
            }
          }}
        />
      </Field>

      <Field title={t('route:place.input_number')}>
        <Table cols={2}>
          <Table.Row className="pt-0">
            <Table.Cell className="pe-3">
              <Input type="number" value={ticketNumber} onChange={(value) => {
                setTicketNumber(getNumber(value));
              }}/>
            </Table.Cell>
            <Table.Cell>
              <Button variant={BUTTON_TYPE.SECONDARY} onClick={handleSearchTicket}>{t('route:button.find')}</Button>
            </Table.Cell>
          </Table.Row>
          <Table.Row colspan={2}>
            <ErrorHint error={error} />
          </Table.Row>
        </Table>
      </Field>

      <Field title={<>{t('route:place.tickets')} — {place.title}</>}></Field>

      <Table cols={4}>
        {/* {tickets.map((ticket) => ( */}
        {tickets.filter((ticket) => ticket.place.id === place.id).map((ticket) => (
          <Fragment key={ticket.number}>
            <Table.Row onClick={() => selectTicket(ticket)}>
              <Table.Cell colspan={4}>
                <div className="place-ticket-header">
                    <b>{t('route:place.ticket', { number: ticket.number })} <span>{ticket.excursion.title}</span></b>
                    <Checkbox.Result className={`mr-1 ${ticket.count.count !== ticket.count.actualCount ? 'not-full' : ''}`} checked={0 < ticket.count.actualCount} />
                </div>
              </Table.Cell>

            </Table.Row>
            <Table.Row onClick={() => selectTicket(ticket)}>
              <Table.Cell colspan={4}>
               <div>
                   <b>{ticket.user.profile.fullName}</b>
               </div>
                <div>
                    <Phone value={ticket.user.phone} />
                </div>
                  <Badge className="mt-2" type={BADGE_TYPE.WARN}>{ticket.comment}</Badge>
              </Table.Cell>
            </Table.Row>
            <Table.Row onClick={() => selectTicket(ticket)}>
              <Table.Cell>
                <InfoDescription description={t('route:place.seat.adult')}>
                  {`${ticket.count.adult !== ticket.count.adultActualCount ? `${ticket.count.adultActualCount}/` : ''}${ticket.count.adult}`}
                </InfoDescription>
              </Table.Cell>
              <Table.Cell>
                <InfoDescription description={t('route:place.seat.child')}>
                  {`${ticket.count.child !== ticket.count.adultActualCount ? `${ticket.count.childActualCount}/` : ''}${ticket.count.child}`}
                </InfoDescription>
              </Table.Cell>
              <Table.Cell>
                <InfoDescription description={t('route:place.seat.noseat')}>
                  {ticket.count.noseat}
                </InfoDescription>
              </Table.Cell>
              <Table.Cell>
                <InfoDescription description={t('route:place.seats')}>
                  {ticket.seats.map((transport, t) => (
                    <div key={t}>{transport.userSeats.sort().join(', ')}</div>
                  ))}
                </InfoDescription>
              </Table.Cell>
            </Table.Row>
            <Table.Row delim={true} />
          </Fragment>
        ))}
      </Table>

      <Modal show={showTicketViewModal} onHide={handleCloseTicketViewModal}>
        <Modal.Header closeButton>
          {ticket && (
            <BackBar title={t('route:ticket_view.title', { number: ticket.number })} onBack={handleCloseTicketViewModal}/>
          )}
        </Modal.Header>
        <Modal.Body>
          {ticket && (
            <TicketView ticket={ticket} schedule={schedule} place={place} onChange={(transport, seats) => {
              setNewSeats({transport, seats});
            }}/>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button.Panel>
            <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handleBoardTicket} disabled={isLoading}>{t('route:button.board')}</Button>
            <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={handleCloseTicketViewModal}>{t('common:button.close')}</Button>
          </Button.Panel>
        </Modal.Footer>
      </Modal>
    </>
  );
};

Place.propTypes = {
  place: PropTypes.any.isRequired,
  schedule: PropTypes.any.isRequired,
  refresh: PropTypes.func,
};

export default Place;